/**
 * The terrain tile art spec (`projects/terrain-tile-art.md` Phase 2.3, `decisions/0184`).
 *
 * A tile's art has two sources, kept apart on purpose:
 *
 * - **Flavour** ({@link TileArt}, one entry per catalogue tile): what the ground is made of. The
 *   surface material, the kind of prop, the cap and face of a raised wall, its height range.
 * - **Mechanics** ({@link artLayers}): which layers draw, derived from the cell's
 *   `tileProperties` at draw time. This is the shared visual language, so a player who has learned
 *   "rough ground" or "big rock" on one tile can read a tile they haven't seen:
 *
 *   | Property | Layer |
 *   | --- | --- |
 *   | difficult (`moveCost > 1`) | the surface is rough and reaches the region's edge |
 *   | soft cover | several small props per cell, short shadows |
 *   | hard cover | one big prop per cell (clustered across merged regions), long shadow |
 *   | impassable, blocks sight | raised: an extruded wall with a face and a cast shadow (ruling 4) |
 *   | impassable, see-through | sunken: terraces down into a pit (ruling 4) |
 *   | impassable | the dark rim; `blocksFlight` doubles it |
 *   | dangerous | an emissive rim tinted by the hazard's damage type |
 *   | blocks sight, walkable | a dark veil over the region |
 *
 * A catalogue edit therefore changes the art with no art edit. Impassable ground draws no surface
 * and no cover props: nothing enters it, and the wall is the cover object. A custom cell uses
 * {@link CUSTOM_ART} and gets every layer its properties call for.
 */
import type { DamageType, EncounterGround, TerrainCell, TerrainProperties, TileTypeId } from "@lancer-vtt/domain";
import { CUSTOM_TILE, DEFAULT_ENCOUNTER_GROUND, DEFAULT_HAZARD_TYPE, tileProperties } from "@lancer-vtt/domain";
import type { RegionParams } from "./geometry";

/**
 * The surfaces in `frontend/public/terrain/atlas.json`, built by `scripts/import-terrain-textures.py`
 * (its `RECIPES`). `tileArt.test.ts` pins this list to the atlas.
 */
export const MATERIAL_IDS = [
  "ground-forest",
  "ground-forest-alt",
  "ground-dust",
  "ground-dust-alt",
  "ground-deck",
  "ground-deck-alt",
  "scrub",
  "brush",
  "bush",
  "bush-dark",
  "pebble-bed",
  "rock-light",
  "rock-dark",
  "scree",
  "mud",
  "cliff",
  "cliff-face",
  "wall-face",
  "metal-dark",
  "metal-bulk",
  "debris",
  "mine-soil",
  "acid",
  "ledge",
  "litter",
  "dry-soil",
  "dead-stalk",
  "bark",
  "bark-dark",
  "bark-cap",
  "lava",
  "oil",
] as const;

export type MaterialId = (typeof MATERIAL_IDS)[number];

/** A map's ground (ruling 8, `EncounterState.ground`): a base surface and a low-frequency second
 *  surface blended over it. */
export type GroundId = EncounterGround;

export const GROUNDS: Record<GroundId, { base: MaterialId; alt: MaterialId }> = {
  "forest-floor": { base: "ground-forest", alt: "ground-forest-alt" },
  dust: { base: "ground-dust", alt: "ground-dust-alt" },
  deck: { base: "ground-deck", alt: "ground-deck-alt" },
};

/** What an encounter with no ground set paints. */
export const DEFAULT_GROUND: GroundId = DEFAULT_ENCOUNTER_GROUND;

/** The object a cover prop depicts. Its size and shadow come from the cover grade, not the kind. */
export type PropKind = "shrub" | "pebble" | "dead-stalk" | "boulder" | "hull-plate" | "trunk" | "log" | "crate" | "container" | "spool";

/** Marks drawn flush with the ground, with no cast shadow, so they never read as cover. */
export type DetailKind = "mine" | "roots" | "branches" | "cables" | "fragments";

/** One tile's flavour. Every field is optional; a missing one falls back to the neutral flavour. */
export type TileArt = {
  /** The region's surface. */
  surface?: MaterialId;
  /** How a surface that is not difficult meets the ground: vegetation feathers, a liquid is a crisp pool. */
  edge?: "feathered" | "pool";
  /** Opacity of a surface that is not difficult (a difficult surface is opaque). */
  surfaceAlpha?: number;
  /** Flush marks over the surface. */
  detail?: DetailKind;
  detailMaterial?: MaterialId;
  /** What a cover prop depicts, and its fill. */
  prop?: PropKind;
  propMaterial?: MaterialId;
  /** Soft cover: small props per cell. */
  propsPerCell?: number;
  /** Hard cover: `false` keeps one prop per cell instead of merging a 2×2 or a pair into one. */
  clustered?: boolean;
  /** Raised impassable: the lifted cap, the south face, and the wall height range in px. */
  cap?: MaterialId;
  face?: MaterialId;
  height?: readonly [number, number];
  /** Raised impassable: a rounder footprint outline (a trunk rather than a rock or a wall). */
  outline?: "round";
  /** Raised impassable: marks on the cap (plate seams on the cell lines, or growth rings). */
  capMarks?: "seams" | "rings";
  /** Sunken impassable: the drop's surface. */
  ledge?: MaterialId;
};

/**
 * The catalogue's flavours. `satisfies` makes a new catalogue tile a type error here until it has
 * art. `open-ground` is never stored, so it has none.
 */
export const TILE_ART = {
  scrub: { surface: "scrub", prop: "shrub", propMaterial: "bush" },
  "scattered-rocks": { surface: "pebble-bed", surfaceAlpha: 0.55, prop: "pebble" },
  "dead-growth": { surface: "litter", prop: "dead-stalk" },
  "thick-brush": { surface: "brush", prop: "shrub", propMaterial: "bush-dark", propsPerCell: 5 },
  scree: { surface: "scree" },
  mud: { surface: "mud" },
  "oil-slick": { surface: "oil" },
  "tangled-roots": { surface: "dry-soil", detail: "roots", detailMaterial: "bark" },
  boulders: { surface: "debris", surfaceAlpha: 0.55, prop: "boulder" },
  trees: { surface: "litter", surfaceAlpha: 0.7, prop: "trunk", clustered: false },
  wreckage: { surface: "debris", prop: "hull-plate" },
  deadfall: { surface: "litter", detail: "branches", detailMaterial: "bark-dark", prop: "log" },
  "rock-outcrop": { cap: "cliff", face: "cliff-face", height: [6, 12] },
  "giant-trunk": { cap: "bark-cap", face: "bark", height: [14, 22], outline: "round", capMarks: "rings" },
  // Floor to ceiling, so one fixed height.
  bulkhead: { cap: "metal-bulk", face: "wall-face", height: [15, 15], capMarks: "seams" },
  // A cliff or cavern wall: the outcrop's rock, taller, since it is the edge of the world.
  "rock-wall": { cap: "rock-dark", face: "cliff-face", height: [16, 24] },
  chasm: { ledge: "ledge" },
  // Shipboard (ruling 9): hangar-bay's catalogue. Crates and cargo stacks stand on the deck with no
  // surface of their own; the difficult two carry the debris bed to their edge.
  crates: { prop: "crate", propsPerCell: 3 },
  "cargo-stacks": { prop: "container" },
  "cable-snarl": { surface: "debris", detail: "cables", prop: "spool", propsPerCell: 2 },
  "loose-debris": { surface: "debris", detail: "fragments", detailMaterial: "metal-dark" },
  "lava-field": { surface: "lava" },
  "acid-pool": { surface: "acid", edge: "pool", surfaceAlpha: 1 },
  minefield: { surface: "mine-soil", surfaceAlpha: 0.9, detail: "mine" },
} as const satisfies Record<Exclude<TileTypeId, "open-ground">, TileArt>;

/** A custom cell's flavour: nothing of its own, so every layer takes the neutral fallback. */
export const CUSTOM_ART: TileArt = {};

/** The neutral flavour each layer falls back to. */
const NEUTRAL = {
  roughSurface: "scree",
  softProp: "pebble",
  hardProp: "boulder",
  cap: "cliff",
  face: "cliff-face",
  height: [6, 12],
  ledge: "ledge",
} as const satisfies Record<string, MaterialId | PropKind | readonly [number, number]>;

const PROP_MATERIAL: Record<PropKind, MaterialId> = {
  shrub: "bush",
  pebble: "rock-light",
  "dead-stalk": "dead-stalk",
  boulder: "rock-dark",
  "hull-plate": "metal-dark",
  trunk: "bark",
  log: "bark",
  crate: "metal-bulk",
  container: "metal-bulk",
  spool: "metal-dark",
};

/**
 * The region outline presets the spec uses. `geometry.test.ts` checks the cell-centre invariant
 * for every one: the 0.5 outline contains a cell's centre exactly when the cell is in the region.
 */
export const REGION_PRESETS = {
  /** A difficult surface: broken ground out to the region's edge. */
  rough: { sigma: 10, amp: 0.14, scale: 40, feather: 0.08 },
  /** Vegetation and scatter: a soft, wide fade. */
  feathered: { sigma: 13, amp: 0.18, scale: 40, feather: 0.18 },
  /** A liquid: a crisp shoreline. */
  pool: { sigma: 10, amp: 0.12, scale: 50, feather: 0.04 },
  /** A raised wall's footprint: nearly cell-exact. */
  raised: { sigma: 5, amp: 0.05, scale: 30, feather: 0.02 },
  /** A trunk's footprint: rounder corners. */
  raisedRound: { sigma: 9, amp: 0.04, scale: 30, feather: 0.02 },
  /** A sunken drop's rim. */
  sunken: { sigma: 9, amp: 0.06, scale: 40, feather: 0 },
} as const satisfies Record<string, RegionParams>;

/** The terraces inside a sunken drop: coverage at this blur, contoured at these levels (not outlines). */
export const TERRACES = { sigma: 26, levels: [0.62, 0.78, 0.9] } as const;

/** A dangerous region's rim colour, by the damage type it deals. */
export const HAZARD_RIM: Record<DamageType, string> = {
  kinetic: "#e6e0d0",
  energy: "#5fd8ff",
  explosive: "#ffb020",
  burn: "#ff6a1f",
  heat: "#ff6a1f",
  variable: "#c890ff",
};

export type SurfaceLayer = {
  material: MaterialId;
  region: RegionParams;
  alpha: number;
  /** Difficult: rough, opaque, to the region's edge. `moveCost` says how difficult. */
  rough: boolean;
  moveCost: number;
};

export type CoverLayer =
  | { grade: "soft"; prop: PropKind; material: MaterialId; size: "small"; shadow: "short"; perCell: number }
  | { grade: "hard"; prop: PropKind; material: MaterialId; size: "big"; shadow: "long"; clustered: boolean };

export type ImpassableLayer =
  | { form: "raised"; cap: MaterialId; face: MaterialId; height: readonly [number, number]; region: RegionParams; capMarks: "seams" | "rings" | null }
  | { form: "sunken"; ledge: MaterialId; region: RegionParams };

/** Everything one region draws. `null` is a layer its properties don't call for. */
export type ArtLayers = {
  surface: SurfaceLayer | null;
  detail: { kind: DetailKind; material: MaterialId | null } | null;
  cover: CoverLayer | null;
  impassable: ImpassableLayer | null;
  /** The dark "can't walk here" rim; `double` when flight is blocked too. */
  rim: "single" | "double" | null;
  hazard: { type: DamageType; color: string } | null;
  /** Blocks sight but can be walked through. */
  veil: boolean;
};

/** The layers a region with flavour `art` and mechanics `props` draws. */
export function artLayers(art: TileArt, props: TerrainProperties): ArtLayers {
  const impassable = props.blocksMovement === true;
  const moveCost = props.moveCost ?? 1;
  const difficult = moveCost > 1;

  let surface: SurfaceLayer | null = null;
  if (!impassable && difficult) {
    surface = { material: art.surface ?? NEUTRAL.roughSurface, region: REGION_PRESETS.rough, alpha: 1, rough: true, moveCost };
  } else if (!impassable && art.surface) {
    surface = { material: art.surface, region: art.edge === "pool" ? REGION_PRESETS.pool : REGION_PRESETS.feathered, alpha: art.surfaceAlpha ?? 0.85, rough: false, moveCost };
  }

  let cover: CoverLayer | null = null;
  if (!impassable && props.cover === "soft") {
    const prop = art.prop ?? NEUTRAL.softProp;
    cover = { grade: "soft", prop, material: art.propMaterial ?? PROP_MATERIAL[prop], size: "small", shadow: "short", perCell: art.propsPerCell ?? 4 };
  } else if (!impassable && props.cover === "hard") {
    const prop = art.prop ?? NEUTRAL.hardProp;
    cover = { grade: "hard", prop, material: art.propMaterial ?? PROP_MATERIAL[prop], size: "big", shadow: "long", clustered: art.clustered ?? true };
  }

  let wall: ImpassableLayer | null = null;
  if (impassable && props.blocksSight === true) {
    wall = {
      form: "raised",
      cap: art.cap ?? NEUTRAL.cap,
      face: art.face ?? NEUTRAL.face,
      height: art.height ?? NEUTRAL.height,
      region: art.outline === "round" ? REGION_PRESETS.raisedRound : REGION_PRESETS.raised,
      capMarks: art.capMarks ?? null,
    };
  } else if (impassable) {
    wall = { form: "sunken", ledge: art.ledge ?? NEUTRAL.ledge, region: REGION_PRESETS.sunken };
  }

  const hazardType = props.hazard ?? DEFAULT_HAZARD_TYPE;
  return {
    surface,
    detail: art.detail ? { kind: art.detail, material: art.detailMaterial ?? null } : null,
    cover,
    impassable: wall,
    rim: impassable ? (props.blocksFlight === true ? "double" : "single") : null,
    hazard: props.dangerous === true ? { type: hazardType, color: HAZARD_RIM[hazardType] } : null,
    veil: !impassable && props.blocksSight === true,
  };
}

/** The flavour of a stored cell: its catalogue tile's, or the neutral custom flavour. */
export function tileArtFor(cell: TerrainCell): TileArt {
  if (cell.tile === CUSTOM_TILE || cell.tile === "open-ground") return CUSTOM_ART;
  return TILE_ART[cell.tile];
}

/** The layers a stored cell draws, read through `tileProperties` at call time. */
export function cellArtLayers(cell: TerrainCell): ArtLayers {
  return artLayers(tileArtFor(cell), tileProperties(cell));
}
